import BannerNotification from '@atoms/BannerNotification/BannerNotification';
import Icon from '@atoms/Icon/Icon';
import { IBannerNotification } from '@models/IBannerNotification';
import { IMessage } from '@models/IMessage';
import { IPricingData } from '@models/IPricingData';
import { IProductInfo } from '@models/IProductDetails';
import { IProductListingStatusLabels } from '@models/IProductListingStatusLabels';
import ProductStockStatusEnum from '@models/enums/ProductStockStatusEnum';
import BulletList from '@molecules/BulletList/BulletList';
import ProductDetailsPricing from '@molecules/ProductDetailsPricing/ProductDetailsPricing';
import ProductDetailsStockStatus from '@molecules/ProductDetailsStockStatus/ProductDetailsStockStatus';

interface Props {
  readonly backOrderText: IMessage;
  readonly bannerNotification?: IBannerNotification;
  readonly copyTaxLabel: string;
  readonly dataTestId?: string;
  readonly deliveryLabel: string;
  readonly ecoTaxLabel: string;
  readonly name: string;
  readonly preOrderText: IMessage;
  readonly pricingData: IPricingData;
  readonly productInfo?: IProductInfo;
  readonly regionalTaxLabel: string;
  readonly skuCode: string;
  readonly skuLabel: string;
  readonly statusLabels: IProductListingStatusLabels;
  readonly usbCharger?: 'included' | 'not_included';
  readonly usbChargerLabel?: string;
  readonly onAddProductToCart: () => Promise<void>;
}

const ProductDetailsSummary = ({
  backOrderText,
  bannerNotification,
  copyTaxLabel,
  dataTestId = 'Product-Details-Summary',
  deliveryLabel,
  ecoTaxLabel,
  name,
  preOrderText,
  pricingData,
  productInfo,
  regionalTaxLabel,
  skuCode,
  skuLabel,
  statusLabels,
  usbCharger,
  usbChargerLabel,
  onAddProductToCart,
}: Props) => {
  const stockStatus = pricingData?.status;
  const hasLimitedDisplay =
    !stockStatus ||
    stockStatus === ProductStockStatusEnum.DoNotShow ||
    stockStatus === ProductStockStatusEnum.BtoBOnly;

  return (
    <div>
      {skuLabel && skuCode && (
        <div
          className="body-three mb-[5px] whitespace-nowrap text-grey-450 lg:mb-5 lg:font-normal"
          data-testid={`${dataTestId}-SKU-Label/Code`}
        >
          {skuLabel}
          {': '}
          {skuCode}
        </div>
      )}

      {name && (
        <h1
          className="h3 mb-5 w-full lg:mb-7.5"
          data-testid={`${dataTestId}-Name`}
        >
          {name}
        </h1>
      )}

      {!hasLimitedDisplay && !!pricingData?.price && (
        <div className="mb-7.5">
          <ProductDetailsPricing
            copyTaxLabel={copyTaxLabel}
            ecoTaxLabel={ecoTaxLabel}
            pricingData={pricingData}
            regionalTaxLabel={regionalTaxLabel}
            dataTestId={`${dataTestId}-Pricing-Top`}
          />
        </div>
      )}

      {usbChargerLabel && usbCharger && (
        <div
          className="mb-7.5 flex gap-2.5"
          data-testid={`${dataTestId}-USB-Charger-Info`}
        >
          <Icon name={`usb_${usbCharger}`} colorClassName="text-grey-400" />
          <span className="body-four text-black-100">{usbChargerLabel}</span>
        </div>
      )}

      {!hasLimitedDisplay && !!pricingData?.price && (
        <div className="mb-7.5">
          <ProductDetailsStockStatus
            deliveryLabel={deliveryLabel}
            backOrderText={backOrderText}
            preOrderText={preOrderText}
            status={stockStatus}
            statusLabels={statusLabels}
            onAddProductToCart={onAddProductToCart}
            dataTestId={`${dataTestId}-Stock-Status`}
          />

          {stockStatus === ProductStockStatusEnum.InStock && (
            <p
              className="body-four"
              data-testid={`${dataTestId}-Delivery-Label`}
            >
              {deliveryLabel}
            </p>
          )}
        </div>
      )}

      {bannerNotification && (
        <div className="mb-10 lg:mb-15">
          <BannerNotification
            displayStyle={bannerNotification.displayStyle}
            iconName={bannerNotification.icon}
            richText={bannerNotification.text}
            link={bannerNotification.link}
            dataTestId={`${dataTestId}-Banner-Notification`}
          />
        </div>
      )}

      {productInfo && (
        <div className="mb-10 lg:mb-15">
          <BulletList
            title={productInfo.title}
            items={productInfo.descriptionItems.map((descriptionItem) => ({
              name: descriptionItem,
            }))}
            itemNameClass="body-two"
            itemsGapClass="gap-y-2.5 lg:gap-y-[5px]"
            bulletMarginClass="mr-[15px] lg:mr-5"
            bulletSizeClass="h-[14px] w-[14px]"
            dataTestId={`${dataTestId}-Product-Info`}
          />
        </div>
      )}
    </div>
  );
};

export default ProductDetailsSummary;
